/**
 * Client-only Hebrew calendar adapter for the transactions board (HAT-1210).
 *
 * Gregorian ISO stays the stored value. This module only prints a Hebrew line
 * (day gematriya + month, or day gematriya alone) from that civil day.
 *
 * Civil day is always Asia/Jerusalem, matching israelCalendarDate.ts:
 * formatToParts + suffixless ISO treated as UTC.
 *
 * Engine: @hebcal/hdate when it can convert the day; Intl `calendar:hebrew`
 * if hebcal throws or returns an unusable month.
 */
import { HDate } from '@hebcal/hdate';

export const HEBREW_CIVIL_TIMEZONE = 'Asia/Jerusalem';

const MIN_REAL_YEAR = 1900;
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_WITHOUT_TZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

/**
 * Hebrew day letters — same table as ViewerCore DateHelper.ToHebrewDate,
 * with Unicode geresh / gershayim (׳ / ״) so ט״ו / ט״ז / ל׳ match print.
 */
export const HEBREW_DAY_LETTERS = [
  'א׳',
  'ב׳',
  'ג׳',
  'ד׳',
  'ה׳',
  'ו׳',
  'ז׳',
  'ח׳',
  'ט׳',
  'י׳',
  'י״א',
  'י״ב',
  'י״ג',
  'י״ד',
  'ט״ו',
  'ט״ז',
  'י״ז',
  'י״ח',
  'י״ט',
  'כ׳',
  'כ״א',
  'כ״ב',
  'כ״ג',
  'כ״ד',
  'כ״ה',
  'כ״ו',
  'כ״ז',
  'כ״ח',
  'כ״ט',
  'ל׳',
] as const;

/** hebcal Nisan=1 month numbers → Hebrew names used on the board (no year). */
const HEBREW_MONTH_NAMES: Record<number, string> = {
  1: 'ניסן',
  2: 'אייר',
  3: 'סיון',
  4: 'תמוז',
  5: 'אב',
  6: 'אלול',
  7: 'תשרי',
  8: 'חשון',
  9: 'כסלו',
  10: 'טבת',
  11: 'שבט',
  12: 'אדר',
  13: 'אדר ב׳',
};

const civilPartsFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: HEBREW_CIVIL_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const hebrewIntlParts = new Intl.DateTimeFormat('he-IL-u-ca-hebrew', {
  timeZone: HEBREW_CIVIL_TIMEZONE,
  day: 'numeric',
  month: 'long',
});

const hebrewYearIntlParts = new Intl.DateTimeFormat('en-u-ca-hebrew', {
  timeZone: HEBREW_CIVIL_TIMEZONE,
  year: 'numeric',
});

const HEBREW_GEMATRIYA_ONES = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'] as const;
const HEBREW_GEMATRIYA_TENS = ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ'] as const;
const HEBREW_GEMATRIYA_HUNDREDS = ['', 'ק', 'ר', 'ש', 'ת', 'תק', 'תר', 'תש', 'תת', 'תתק'] as const;

/** ~1 Jan 2000 — same civil floor as the Gregorian year-jump list. */
export const EARLIEST_HEBREW_SELECTABLE_YEAR = 5760;

export type HebrewDateParts = {
  iso: string;
  day: number;
  month: number;
  year: number;
  dayGematriya: string;
  monthName: string;
  /** Variant B board line — «כ״ו אלול», no Hebrew year. */
  boardLine: string;
};

export type HebrewPickerHeader = {
  month: number;
  monthName: string;
  year: number;
  yearGematriya: string;
};

export type HebrewMonthYear = {
  year: number;
  month: number;
};

const cache = new Map<string, HebrewDateParts | null>();

const pad2 = (value: number): string => String(value).padStart(2, '0');

const gematriyaForDay = (day: number): string => {
  if (!Number.isInteger(day) || day < 1 || day > HEBREW_DAY_LETTERS.length) return '';
  return HEBREW_DAY_LETTERS[day - 1];
};

const boardLine = (dayGematriya: string, monthName: string): string => {
  if (!dayGematriya || !monthName) return '';
  return `${dayGematriya} ${monthName}`;
};

const applyGershayim = (letters: string): string => {
  if (!letters) return '';
  if (letters.length === 1) return `${letters}׳`;
  return `${letters.slice(0, -1)}״${letters.slice(-1)}`;
};

/**
 * Hebrew year letters without the thousands (ה׳): 5786 → תשפ״ו.
 * Same geresh / gershayim marks as the day table.
 */
export const formatHebrewYearGematriya = (year: number): string => {
  if (!Number.isInteger(year) || year < 1) return '';
  const n = year % 1000;
  if (n <= 0) return '';
  const hundreds = Math.floor(n / 100);
  const remainder = n % 100;
  const tail =
    remainder === 15 ? 'טו' : remainder === 16 ? 'טז' : `${HEBREW_GEMATRIYA_TENS[Math.floor(remainder / 10)]}${HEBREW_GEMATRIYA_ONES[remainder % 10]}`;
  return applyGershayim(`${HEBREW_GEMATRIYA_HUNDREDS[hundreds]}${tail}`);
};

const hebrewYearViaIntl = (civilIso: string): number => {
  const [year, month, day] = civilIso.split('-').map(Number);
  if (!year || !month || !day) return 0;
  const instant = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const raw = hebrewYearIntlParts.formatToParts(instant).find((part) => part.type === 'year')?.value ?? '';
  const parsed = Number(raw.replace(/[^\d]/g, ''));
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
};

/** Adar II (13) is not a month in a common year — land on Adar. */
export const clampHebrewMonthForYear = (month: number, year: number): number => {
  if (month !== 13) return month;
  try {
    if (!new HDate(1, 7, year).isLeapYear()) return 12;
  } catch {
    return 12;
  }
  return month;
};

/** 1st of that Hebrew month/year as a local civil Date for react-datepicker. */
export const civilDateFromHebrew = (year: number, month: number, day = 1): Date | null => {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  try {
    const hd = new HDate(day, clampHebrewMonthForYear(month, year), year);
    const greg = hd.greg();
    if (!greg || Number.isNaN(greg.getTime())) return null;
    return new Date(greg.getFullYear(), greg.getMonth(), greg.getDate());
  } catch {
    return null;
  }
};

export const selectableHebrewYears = (shownYear: number): number[] => {
  const todayYear = getHebrewDateParts(new Date())?.year ?? shownYear;
  const latest = Math.max(todayYear + 1, shownYear + 1);
  const earliest = Math.min(EARLIEST_HEBREW_SELECTABLE_YEAR, shownYear);
  return Array.from({ length: latest - earliest + 1 }, (_, offset) => latest - offset);
};

export const hebrewPickerHeaderFromLocalCivilDate = (date: Date): HebrewPickerHeader | null => {
  const iso = civilIsoFromLocalDate(date);
  const parts = getHebrewDateParts(iso);
  if (!parts?.monthName) return null;
  const year = parts.year || hebrewYearViaIntl(iso);
  if (!year) return null;
  return {
    month: parts.month,
    monthName: parts.monthName,
    year,
    yearGematriya: formatHebrewYearGematriya(year),
  };
};

export const hebrewPickerHeaderFromHebrewMonth = (year: number, month: number): HebrewPickerHeader | null => {
  const civil = civilDateFromHebrew(year, month, 1);
  return civil ? hebrewPickerHeaderFromLocalCivilDate(civil) : null;
};

/** True if any civil day of the viewed Gregorian month is in that Hebrew month/year. */
export const gregorianMonthContainsHebrewMonth = (viewed: Date, hebrewYear: number, hebrewMonth: number): boolean => {
  const year = viewed.getFullYear();
  const month = viewed.getMonth();
  const last = new Date(year, month + 1, 0).getDate();
  for (let day = 1; day <= last; day += 1) {
    const parts = getHebrewDateParts(civilIsoFromParts(year, month + 1, day));
    if (parts?.year === hebrewYear && parts.month === hebrewMonth) return true;
  }
  return false;
};

/**
 * Same Hebrew month and day in the target year (clamped to that month's
 * length, and Adar II → Adar in a common year). The civil date is a real
 * day in the Hebrew month — not a Gregorian 1st that may sit outside it
 * (Tishrei 5788 is Oct 2–31 2027; Oct 1 2027 is still Elul 5787).
 */
export const hebrewYearJumpAnchorFromParts = (month: number, day: number, targetHebrewYear: number): Date | null => {
  if (!Number.isInteger(month) || month < 1) return null;
  const clampedMonth = clampHebrewMonthForYear(month, targetHebrewYear);
  let clampedDay = Math.max(Number.isInteger(day) ? day : 1, 1);
  try {
    clampedDay = Math.min(clampedDay, HDate.daysInMonth(clampedMonth, targetHebrewYear));
  } catch {
    clampedDay = Math.min(clampedDay, 29);
  }
  return civilDateFromHebrew(targetHebrewYear, clampedMonth, clampedDay);
};

export const hebrewYearJumpAnchor = (from: Date, targetHebrewYear: number): Date | null => {
  const parts = getHebrewDateParts(civilIsoFromLocalDate(from));
  if (!parts?.month || !parts.year) return null;
  return hebrewYearJumpAnchorFromParts(parts.month, parts.day, targetHebrewYear);
};

export const hebrewMonthYearFromCivilDate = (date: Date): HebrewMonthYear | null => {
  const parts = getHebrewDateParts(civilIsoFromLocalDate(date));
  if (!parts?.month || !parts.year) return null;
  return { year: parts.year, month: parts.month };
};

/** Header for the viewed picker month, preferring a year-jump Hebrew anchor when it is still on screen. */
export const resolveHebrewPickerHeader = (viewed: Date, jump: HebrewMonthYear | null): HebrewPickerHeader | null => {
  if (jump && gregorianMonthContainsHebrewMonth(viewed, jump.year, jump.month)) {
    return hebrewPickerHeaderFromHebrewMonth(jump.year, jump.month);
  }
  return hebrewPickerHeaderFromLocalCivilDate(viewed);
};

export const civilIsoFromParts = (year: number, month: number, day: number): string => `${year}-${pad2(month)}-${pad2(day)}`;

/**
 * react-datepicker hands a Date whose local Y/M/D is the cell's civil day.
 * That instant is not a Jerusalem day (Tokyo midnight of Sep 12 is still
 * Sep 11 in Israel). Always convert the picker's local parts to date-only ISO
 * before Hebrew formatting.
 */
export const civilIsoFromLocalDate = (date: Date): string => civilIsoFromParts(date.getFullYear(), date.getMonth() + 1, date.getDate());

export const formatHebrewFromLocalCivilDate = (date: Date): string => formatHebrewBoardDate(civilIsoFromLocalDate(date));

export const formatHebrewDayFromLocalCivilDate = (date: Date): string => formatHebrewDayGematriya(civilIsoFromLocalDate(date));

export const jerusalemCivilParts = (date: Date): { year: number; month: number; day: number; iso: string } => {
  const parts = civilPartsFormat.formatToParts(date);
  const year = Number(parts.find((part) => part.type === 'year')?.value ?? '');
  const month = Number(parts.find((part) => part.type === 'month')?.value ?? '');
  const day = Number(parts.find((part) => part.type === 'day')?.value ?? '');
  return { year, month, day, iso: civilIsoFromParts(year, month, day) };
};

/**
 * Instant used only to fold a value onto a Jerusalem civil day.
 * Date-only ISO is that Jerusalem day (noon UTC, still that day in IL).
 * Suffixless `YYYY-MM-DDTHH:mm[:ss]` is treated as UTC, like israelCalendarDate.ts.
 */
export const toJerusalemInstant = (value: unknown): Date | null => {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    if (jerusalemCivilParts(value).year < MIN_REAL_YEAR) return null;
    return value;
  }

  if (typeof value !== 'string') {
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? null : toJerusalemInstant(date);
  }

  const trimmed = value.trim();
  const dateOnly = DATE_ONLY.exec(trimmed);
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]);
    const day = Number(dateOnly[3]);
    if (year < MIN_REAL_YEAR) return null;
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  }

  const normalized = ISO_WITHOUT_TZ.test(trimmed) ? `${trimmed}Z` : trimmed;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;
  if (jerusalemCivilParts(date).year < MIN_REAL_YEAR) return null;
  return date;
};

export const toJerusalemCivilIso = (value: unknown): string | null => {
  const instant = toJerusalemInstant(value);
  if (!instant) return null;
  const civil = jerusalemCivilParts(instant);
  if (!civil.year || !civil.month || !civil.day) return null;
  return civil.iso;
};

export const hebrewMonthName = (month: number, isLeap: boolean): string => {
  if (month === 12 && isLeap) return 'אדר א׳';
  return HEBREW_MONTH_NAMES[month] ?? '';
};

/** Intl fallback: day number + long month, then our gematriya table. */
export const hebrewDatePartsViaIntl = (civilIso: string): HebrewDateParts | null => {
  const [year, month, day] = civilIso.split('-').map(Number);
  if (!year || !month || !day) return null;
  const instant = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const parts = hebrewIntlParts.formatToParts(instant);
  const dayNum = Number(parts.find((part) => part.type === 'day')?.value);
  const rawMonth = parts.find((part) => part.type === 'month')?.value ?? '';
  const dayGematriya = gematriyaForDay(dayNum);
  if (!dayGematriya || !rawMonth) return null;
  const monthName = rawMonth.replace(/^ב(?=\S)/, '');
  return {
    iso: civilIso,
    day: dayNum,
    month: 0,
    year: hebrewYearViaIntl(civilIso),
    dayGematriya,
    monthName,
    boardLine: boardLine(dayGematriya, monthName),
  };
};

export const hebrewDatePartsViaHebcal = (civilIso: string): HebrewDateParts | null => {
  const [year, month, day] = civilIso.split('-').map(Number);
  if (!year || !month || !day) return null;
  // Local Y-M-D so HDate(Date) ignores the browser TZ instant and uses this civil day.
  const hd = new HDate(new Date(year, month - 1, day));
  const hebrewDay = hd.getDate();
  const hebrewMonth = hd.getMonth();
  const hebrewYear = hd.getFullYear();
  const dayGematriya = gematriyaForDay(hebrewDay);
  const monthName = hebrewMonthName(hebrewMonth, hd.isLeapYear());
  if (!dayGematriya || !monthName) return null;
  return {
    iso: civilIso,
    day: hebrewDay,
    month: hebrewMonth,
    year: hebrewYear,
    dayGematriya,
    monthName,
    boardLine: boardLine(dayGematriya, monthName),
  };
};

const computeHebrewDateParts = (civilIso: string): HebrewDateParts | null => {
  try {
    const fromHebcal = hebrewDatePartsViaHebcal(civilIso);
    if (fromHebcal) return fromHebcal;
  } catch {
    // Adapter: a hebcal failure must not blank the board — Intl still knows the day.
  }
  try {
    return hebrewDatePartsViaIntl(civilIso);
  } catch {
    return null;
  }
};

/** Cached isoDay → parts for the virtualized transactions board. */
export const getHebrewDateParts = (value: unknown): HebrewDateParts | null => {
  const iso = toJerusalemCivilIso(value);
  if (!iso) return null;
  if (cache.has(iso)) return cache.get(iso) ?? null;
  const parts = computeHebrewDateParts(iso);
  cache.set(iso, parts);
  return parts;
};

export const formatHebrewBoardDate = (value: unknown): string => getHebrewDateParts(value)?.boardLine ?? '';

export const formatHebrewDayGematriya = (value: unknown): string => getHebrewDateParts(value)?.dayGematriya ?? '';

/** Same printable line the board used before the helper was split out. */
export const formatHebrewTransactionDate = (value: unknown): string => formatHebrewBoardDate(value);

export const clearHebrewDateCache = (): void => {
  cache.clear();
};
